import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormsModule } from '@angular/forms';
import {
  GoogleMap,
  GoogleMapsModule,
  MapAdvancedMarker,
  MapInfoWindow,
} from '@angular/google-maps';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { Group } from '../../../models/group.model';
import { UserModel } from '../../../models/user.model';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { SearchService } from '../../../services/search.service';
import * as memberActions from '../../../state/actions/member.actions';
import { AppState } from '../../../state/appstate';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../components/buttons/btn-chip/btn-chip.component';
import { CheckboxComponent } from '../../components/inputs/checkbox/checkbox.component';
import { environment } from '../../../../environments/environment';

export interface SearchItem {
  id: string;
  name: string;
  imageUrl?: string;
  city?: string;
  groupType: 'church' | 'school' | 'user';
  location?: {
    position?: {
      latitude: number;
      longitude: number;
    };
  };
  email?: string;
  description?: string;
  raw: any;
}

function getDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Radius of the earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

@Component({
  selector: 'app-search-communities',
  standalone: true,
  templateUrl: './search-communities.component.html',
  imports: [
    AsyncPipe,
    RouterLink,
    GoogleMapsModule,
    FormsModule,
    InitialsPipe,
    CheckboxComponent,
    BtnChipComponent,
    BtnActionComponent,
    BtnBackComponent,
    BtnActionComponent,
  ],
})
export class SearchCommunitiesComponent implements OnInit {
  router = inject(Router);
  http = inject(HttpClient);
  searchService = inject(SearchService);
  cdr = inject(ChangeDetectorRef);
  store = inject(Store<AppState>);
  afs = inject(AngularFirestore);

  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChildren(MapAdvancedMarker) mapMarkers!: QueryList<MapAdvancedMarker>;

  user: UserModel | undefined | null;

  nearbyGroups: Group[] = [];
  loadingNearby: boolean = false;
  nearbyError: string | null = null;
  locationSource: string = '';
  searchText: string = '';
  distance: string = '25km';
  availableDistances = ['5km', '10km', '25km', '50km', '100km'];

  // Geolocation and search state variables
  searchNearMe: boolean = true;
  userLocation: google.maps.LatLngLiteral | null = null;
  showLocationModal: boolean = false;
  locationPermissionDenied: boolean = false;
  private locationInitialized: boolean = false;
  selectedFilters: ('all' | 'church' | 'school' | 'user')[] = ['all'];
  hasSearched: boolean = false;
  unifiedResults: SearchItem[] = [];
  filteredResults: SearchItem[] = [];
  infoWindowGroup: SearchItem | null = null;

  mapOptions: google.maps.MapOptions = {
    mapId: 'b45898a1f3fbad8919f6191f',
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    zoom: 11,
    fullscreenControl: false,
    streetViewControl: false,
  };
  mapCenter: google.maps.LatLngLiteral = { lat: 25.2697, lng: 55.3095 };
  userLocationMarkerContent: HTMLElement | null = null;
  markers: any[] = [];
  selectedGroup: SearchItem | null = null;
  activeDetailItem: SearchItem | null = null;
  joinedGroups: Set<string> = new Set();

  whatsAppMessage: string = `Hi! I am using AtChurch to connect and find communities, activities, and events around me. Check it out and join here: https://atchurch.app`;
  whatsAppInviteLink: string = `https://api.whatsapp.com/send?text=${encodeURIComponent(this.whatsAppMessage)}`;

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(take(2)) // take until destroyed / just read initially
      .subscribe((user) => {
        this.user = user;
        if (
          user &&
          this.unifiedResults.length === 0 &&
          !this.loadingNearby &&
          !this.locationInitialized
        ) {
          // On profile load, try loading location
          this.initLocationAndPage();
        }
      });
  }

  ngOnInit(): void {
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.className = 'relative flex items-center justify-center h-4 w-4';

      const pulse = document.createElement('div');
      pulse.className =
        'absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping';

      const dot = document.createElement('div');
      dot.className =
        'relative inline-flex rounded-full h-3 w-3 bg-blue-600 border border-white';

      div.appendChild(pulse);
      div.appendChild(dot);
      this.userLocationMarkerContent = div;
    }
    this.initLocationAndPage();
  }

  getUserMarkerOptions(): google.maps.MarkerOptions {
    if (
      typeof google !== 'undefined' &&
      google.maps &&
      google.maps.SymbolPath
    ) {
      return {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7.5,
          fillColor: '#2563EB',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        title: 'Your Current Location',
        zIndex: 9999,
      };
    }
    return { title: 'Your Current Location' };
  }

  getChurchMarkerIcon(): google.maps.Icon | string {
    if (typeof google !== 'undefined' && google.maps && google.maps.Size) {
      return {
        url: 'assets/markers/marker_church.png',
        scaledSize: new google.maps.Size(28, 34),
      };
    }
    return 'assets/markers/marker_church.png';
  }

  async initLocationAndPage() {
    if (this.locationInitialized) return;
    this.locationInitialized = true;
    this.loadingNearby = true;
    this.nearbyError = null;
    this.selectedGroup = null;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.searchNearMe = true;
          this.showLocationModal = false;
          this.locationPermissionDenied = false;
          this.mapCenter = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.userLocation = { ...this.mapCenter };
          this.mapOptions = {
            ...this.mapOptions,
            center: this.mapCenter,
          };
          this.loadingNearby = false;
          // Initial load: center map at user's location (do not auto-run search)
          if (this.googleMap?.googleMap) {
            this.googleMap.googleMap.panTo(this.mapCenter);
          }
          this.cdr.detectChanges();
        },
        (error) => {
          console.warn('Geolocation failed on load:', error);
          this.searchNearMe = false;
          this.locationPermissionDenied =
            error.code === error.PERMISSION_DENIED;
          if (this.locationPermissionDenied) {
            this.nearbyError =
              'Location is disabled. Please enable location permissions to search near you.';
            this.showLocationModal = true;
          } else {
            this.nearbyError =
              'Precise location is unavailable. Using approximate location instead.';
            this.showLocationModal = false;
          }
          this.resolveIpLocation(false); // resolve location but don't run search automatically
        },
        { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 },
      );
    } else {
      this.searchNearMe = false;
      this.nearbyError = 'Geolocation is not supported by your browser.';
      this.showLocationModal = true;
      this.resolveIpLocation(false);
    }
  }

  onSearchNearMeChange(checked: any) {
    this.searchNearMe = !!checked;
    if (this.searchNearMe) {
      this.enableLocationAndSearch();
    } else {
      this.nearbyError = null;
      // Do not search immediately, just keep checked state sync
    }
  }

  openLocationModal() {
    this.showLocationModal = true;
    this.cdr.detectChanges();
  }

  closeLocationModal() {
    this.showLocationModal = false;
    this.cdr.detectChanges();
  }

  useApproximateLocation() {
    this.showLocationModal = false;
    this.searchNearMe = false;
    this.resolveIpLocation(true);
  }

  enableLocationAndSearch() {
    this.nearbyError = null;
    if (!navigator.geolocation) {
      this.nearbyError = 'Geolocation is not supported by your browser.';
      this.searchNearMe = false;
      this.showLocationModal = true;
      return;
    }
    this.loadingNearby = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.searchNearMe = true;
        this.showLocationModal = false;
        this.locationPermissionDenied = false;
        this.mapCenter = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.userLocation = { ...this.mapCenter };
        this.mapOptions = {
          ...this.mapOptions,
          center: this.mapCenter,
        };
        this.searchNearby(
          this.mapCenter.lat,
          this.mapCenter.lng,
          'Browser Geolocation',
        );
      },
      (error) => {
        console.warn('Geolocation prompt failed or denied:', error);
        this.locationPermissionDenied = error.code === error.PERMISSION_DENIED;
        if (this.locationPermissionDenied) {
          this.nearbyError =
            'Please enable location permissions in your browser settings to search near you.';
          this.searchNearMe = false;
          this.loadingNearby = false;
          this.showLocationModal = true;
        } else {
          this.nearbyError =
            'Precise location is unavailable. Using approximate location instead.';
          this.useApproximateLocation();
        }
        this.cdr.detectChanges();
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 },
    );
  }

  async resolveIpLocation(triggerSearch: boolean = false) {
    try {
      const res = await lastValueFrom(
        this.http.get<any>('https://ipapi.co/json/').pipe(take(1)),
      );
      if (res && res.latitude && res.longitude) {
        this.mapCenter = { lat: res.latitude, lng: res.longitude };
        this.userLocation = { ...this.mapCenter };
        this.mapOptions = { ...this.mapOptions, center: this.mapCenter };
        if (triggerSearch) {
          this.searchNearby(res.latitude, res.longitude, 'IP Geolocation');
        } else {
          this.loadingNearby = false;
          this.cdr.detectChanges();
        }
      } else {
        throw new Error('Invalid IP location format');
      }
    } catch (err) {
      console.warn(
        'IP location resolution failed, trying Profile location:',
        err,
      );
      this.resolveProfileLocation(triggerSearch);
    }
  }

  async resolveProfileLocation(triggerSearch: boolean = false) {
    const city = this.user?.address?.city;
    const country = this.user?.address?.country;
    if (city || country) {
      const addressStr = [city, country].filter(Boolean).join(', ');
      try {
        const coords = await this.geocodeAddress(addressStr);
        this.mapCenter = coords;
        this.userLocation = { ...coords };
        this.mapOptions = { ...this.mapOptions, center: this.mapCenter };
        if (triggerSearch) {
          this.searchNearby(
            coords.lat,
            coords.lng,
            `Profile Location (${addressStr})`,
          );
        } else {
          this.loadingNearby = false;
          this.cdr.detectChanges();
        }
      } catch (err) {
        console.warn('Profile geocoding failed, using default location:', err);
        this.useDefaultLocation(triggerSearch);
      }
    } else {
      console.warn('No location info in user profile, using default location.');
      this.useDefaultLocation(triggerSearch);
    }
  }

  useDefaultLocation(triggerSearch: boolean = false) {
    this.mapCenter = { lat: 25.2697, lng: 55.3095 };
    this.mapOptions = { ...this.mapOptions, center: this.mapCenter };
    if (triggerSearch) {
      this.searchNearby(25.2697, 55.3095, 'Default Location (Dubai)');
    } else {
      this.loadingNearby = false;
      this.cdr.detectChanges();
    }
  }

  geocodeAddress(addressStr: string): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined' || !google.maps) {
        reject(new Error('Google Maps API not loaded'));
        return;
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addressStr }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          resolve({ lat, lng });
        } else {
          reject(new Error(`Geocoding failed with status: ${status}`));
        }
      });
    });
  }

  async searchNearby(lat: number, lon: number, source: string) {
    this.loadingNearby = true;
    this.nearbyError = null;
    this.mapCenter = { lat, lng: lon };
    this.locationSource = source;
    this.hasSearched = true;

    this.mapOptions = {
      ...this.mapOptions,
      center: this.mapCenter,
    };

    try {
      // 1. Fetch groups from backend API
      let groupItems: SearchItem[] = [];
      try {
        const groups = await this.searchService.getGroupsByLocation(
          lat,
          lon,
          this.distance,
          50,
        );
        groupItems = groups.map((g) => ({
          id: g.id,
          name: g.name,
          imageUrl: g.imageUrl,
          city: g.address?.city,
          groupType: g.groupType === 'school' ? 'school' : 'church',
          location: g.location,
          email: g.email,
          description: g.description,
          raw: g,
        }));
      } catch (apiErr) {
        console.warn(
          'AWS location search API failed, falling back to Firestore groups:',
          apiErr,
        );
      }

      // Fallback: If API returned 0 items or threw error, fetch directly from Firestore groups & calculate distance
      if (groupItems.length === 0) {
        try {
          const groupsSnapshot = await lastValueFrom(
            this.afs.collection<Group>('groups').get().pipe(take(1)),
          );
          const limitDistanceKm = parseFloat(this.distance.replace('km', ''));
          const rawGroups = groupsSnapshot.docs.map((doc) => {
            const data = doc.data() as Group;
            return {
              ...data,
              id: doc.id,
            };
          });

          const nearbyGroups = rawGroups.filter((g) => {
            const loc = g.location as any;
            const gLat = loc?.position?.latitude;
            const gLng = loc?.position?.longitude;
            if (gLat && gLng) {
              const dist = getDistance(lat, lon, gLat, gLng);
              return dist <= limitDistanceKm;
            }
            return false;
          });

          groupItems = nearbyGroups.map((g) => ({
            id: g.id,
            name: g.name,
            imageUrl: g.imageUrl,
            city: g.address?.city,
            groupType: g.groupType === 'school' ? 'school' : 'church',
            location: g.location,
            email: g.email,
            description: g.description,
            raw: g,
          }));
        } catch (fsGroupErr) {
          console.error(
            'Failed to load fallback Firestore groups:',
            fsGroupErr,
          );
        }
      }

      // 2. Fetch searchable users from Firestore
      let userItems: SearchItem[] = [];
      try {
        const usersSnapshot = await lastValueFrom(
          this.afs
            .collection<UserModel>('users', (ref) =>
              ref.where('isSearchable', '==', true),
            )
            .get()
            .pipe(take(1)),
        );

        const limitDistanceKm = parseFloat(this.distance.replace('km', ''));
        const rawUsers = usersSnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        const nearbyUsers = rawUsers.filter((u) => {
          const loc = u.location as any;
          const uLat = loc?.position?.latitude;
          const uLng = loc?.position?.longitude;
          if (uLat && uLng) {
            const dist = getDistance(lat, lon, uLat, uLng);
            return dist <= limitDistanceKm;
          }
          return false;
        });

        userItems = nearbyUsers.map((u) => ({
          id: u.uid || '',
          name: u.name || 'Anonymous User',
          imageUrl: u.photoURL,
          city: u.address?.city,
          groupType: 'user',
          location: u.location as any,
          email: u.email,
          description: u.profession || 'AtChurch Member',
          raw: u,
        }));
      } catch (userErr) {
        console.error('Failed to load searchable users:', userErr);
      }

      // Combine both lists
      let combined = [...groupItems, ...userItems];

      // Filter by searchText if present (in case searchNearMe is checked)
      if (this.searchText && this.searchText.trim()) {
        const lowerQuery = this.searchText.toLowerCase().trim();
        combined = combined.filter(
          (item) =>
            item.name?.toLowerCase().includes(lowerQuery) ||
            item.description?.toLowerCase().includes(lowerQuery),
        );
      }

      this.unifiedResults = combined;
      this.applyFilter();

      this.loadingNearby = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Failed to load nearby communities:', err);
      this.unifiedResults = [];
      this.filteredResults = [];
      this.markers = [];
      this.loadingNearby = false;
      this.cdr.detectChanges();
    }
  }

  async searchGloballyByText(query: string) {
    this.loadingNearby = true;
    this.nearbyError = null;
    this.hasSearched = true;

    try {
      const lowerQuery = query.toLowerCase().trim();

      // 1. Fetch all groups from Firestore and filter
      const groupsSnapshot = await lastValueFrom(
        this.afs.collection<Group>('groups').get().pipe(take(1)),
      );
      const allGroups = groupsSnapshot.docs.map((doc) => {
        const data = doc.data() as Group;
        data.id = doc.id;
        return data;
      });

      const matchedGroups = allGroups.filter(
        (g) =>
          g.name?.toLowerCase().includes(lowerQuery) ||
          g.description?.toLowerCase().includes(lowerQuery),
      );

      const groupItems: SearchItem[] = matchedGroups.map((g) => ({
        id: g.id,
        name: g.name,
        imageUrl: g.imageUrl,
        city: g.address?.city,
        groupType: g.groupType === 'school' ? 'school' : 'church',
        location: g.location,
        email: g.email,
        description: g.description,
        raw: g,
      }));

      // 2. Fetch all searchable users from Firestore and filter
      let userItems: SearchItem[] = [];
      try {
        const usersSnapshot = await lastValueFrom(
          this.afs
            .collection<UserModel>('users', (ref) =>
              ref.where('isSearchable', '==', true),
            )
            .get()
            .pipe(take(1)),
        );

        const allUsers = usersSnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        const matchedUsers = allUsers.filter(
          (u) =>
            u.name?.toLowerCase().includes(lowerQuery) ||
            u.profession?.toLowerCase().includes(lowerQuery),
        );

        userItems = matchedUsers.map((u) => ({
          id: u.uid || '',
          name: u.name || 'Anonymous User',
          imageUrl: u.photoURL,
          city: u.address?.city,
          groupType: 'user',
          location: u.location as any,
          email: u.email,
          description: u.profession || 'AtChurch Member',
          raw: u,
        }));
      } catch (userErr) {
        console.error('Failed to load searchable users:', userErr);
      }

      this.unifiedResults = [...groupItems, ...userItems];
      this.applyFilter();

      // If we have items with location, center the map on the first one
      const itemWithLoc = this.unifiedResults.find(
        (item) =>
          item.location?.position?.latitude &&
          item.location?.position?.longitude,
      );
      if (itemWithLoc) {
        this.mapCenter = {
          lat: itemWithLoc.location!.position!.latitude,
          lng: itemWithLoc.location!.position!.longitude,
        };
        this.mapOptions = { ...this.mapOptions, center: this.mapCenter };
      }

      this.loadingNearby = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Global search failed:', err);
      this.unifiedResults = [];
      this.filteredResults = [];
      this.markers = [];
      this.loadingNearby = false;
      this.cdr.detectChanges();
    }
  }

  applyFilter() {
    if (this.selectedFilters.includes('all')) {
      this.filteredResults = this.unifiedResults;
    } else {
      this.filteredResults = this.unifiedResults.filter((item) =>
        this.selectedFilters.includes(item.groupType),
      );
    }

    // Map to markers
    this.markers = this.filteredResults
      .filter(
        (item) =>
          item.location?.position?.latitude &&
          item.location?.position?.longitude,
      )
      .map((item) => {
        const img =
          typeof document !== 'undefined'
            ? document.createElement('img')
            : null;
        if (img) {
          img.src = 'assets/markers/marker_church.png';
          img.style.width = '28px';
          img.style.height = '34px';
        }
        return {
          id: item.id,
          position: {
            lat: item.location!.position!.latitude,
            lng: item.location!.position!.longitude,
          },
          title: item.name,
          content: img,
          item: item,
        };
      });
  }

  setFilter(filter: 'all' | 'church' | 'school' | 'user') {
    if (filter === 'all') {
      this.selectedFilters = ['all'];
    } else {
      // Remove 'all' if it exists
      this.selectedFilters = this.selectedFilters.filter((f) => f !== 'all');

      // Toggle the filter
      if (this.selectedFilters.includes(filter)) {
        this.selectedFilters = this.selectedFilters.filter((f) => f !== filter);
      } else {
        this.selectedFilters.push(filter);
      }

      // If nothing is selected, default back to 'all'
      if (this.selectedFilters.length === 0) {
        this.selectedFilters = ['all'];
      }
    }
    this.applyFilter();
    this.selectedGroup = null;
    this.cdr.detectChanges();
  }

  onSearchLocation() {
    this.nearbyError = null;
    const query = this.searchText.trim();

    if (this.searchNearMe) {
      // If search near me is checked, perform search based on coordinates
      this.searchNearby(
        this.mapCenter.lat,
        this.mapCenter.lng,
        'Browser Geolocation',
      );
    } else {
      if (!query) {
        this.nearbyError = 'Please enter a search term.';
        this.cdr.detectChanges();
        return;
      }
      this.loadingNearby = true;
      this.geocodeAddress(query)
        .then((coords) => {
          this.searchNearby(
            coords.lat,
            coords.lng,
            `Location Query: "${query}"`,
          );
        })
        .catch((err) => {
          console.warn(
            'Geocoding failed for search term, attempting global text search...',
            err,
          );
          this.searchGloballyByText(query);
        });
    }
  }

  onDistanceChange() {
    this.searchNearby(
      this.mapCenter.lat,
      this.mapCenter.lng,
      this.locationSource,
    );
  }

  selectGroup(item: SearchItem, markerComponent?: MapAdvancedMarker) {
    this.selectedGroup = item;
    if (
      item.location?.position?.latitude &&
      item.location?.position?.longitude
    ) {
      this.mapCenter = {
        lat: item.location.position.latitude,
        lng: item.location.position.longitude,
      };
      this.mapOptions = {
        ...this.mapOptions,
        center: this.mapCenter,
        zoom: 13,
      };
    }

    // Open info window
    if (markerComponent) {
      this.infoWindowGroup = item;
      this.infoWindow.open(markerComponent);
    } else {
      // Look up MapMarker dynamically
      const matchedMarker = this.mapMarkers.find((m) => {
        const pos = m.position;
        if (!pos) return false;

        let lat: number;
        let lng: number;
        if (typeof pos.lat === 'function') {
          lat = (pos as google.maps.LatLng).lat();
          lng = (pos as google.maps.LatLng).lng();
        } else {
          lat = (pos as google.maps.LatLngLiteral).lat;
          lng = (pos as google.maps.LatLngLiteral).lng;
        }

        const itemLat = item.location?.position?.latitude;
        const itemLng = item.location?.position?.longitude;
        return (
          itemLat !== undefined &&
          itemLng !== undefined &&
          Math.abs(lat - itemLat) < 0.0001 &&
          Math.abs(lng - itemLng) < 0.0001
        );
      });
      if (matchedMarker) {
        this.infoWindowGroup = item;
        this.infoWindow.open(matchedMarker);
      }
    }

    // Scroll list to item
    setTimeout(() => {
      const element = document.getElementById(`item-${item.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);

    this.cdr.detectChanges();
  }

  onClick(item: SearchItem) {
    this.activeDetailItem = item;
    this.cdr.detectChanges();
  }

  closePopup() {
    this.activeDetailItem = null;
    this.cdr.detectChanges();
  }

  joinGroup(item: SearchItem) {
    if (!this.user?.uid) {
      localStorage.setItem('redirectAfterLogin', this.router.url);
      this.router.navigate(['/auth/login']);
      return;
    }
    this.store.dispatch(
      memberActions.requestMembership({
        group: item.raw as Group,
        uid: this.user.uid,
      }),
    );
    this.joinedGroups.add(item.id);
    this.cdr.detectChanges();
  }

  getShareLink(item: SearchItem): string {
    const text = encodeURIComponent(
      `Check out this community on AtChurch: ${item.name} (${item.city || ''}). Find more details here: https://${environment.domain}/links/groupinvite?groupId=${item.id}`,
    );
    return `https://api.whatsapp.com/send?text=${text}`;
  }
}
